import type { FunctionComponent, VoidFunctionComponent } from 'react';

import { VCS_APP_DISPLAY } from '../../../../../shared/vcs/githubApp/apps';
import Badge from '../../../generic/Badge/Badge';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import Icon, { icons } from '../../../generic/Icon';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';
import type { IconClassName } from '../../../generic/Icon/icons';

import styles from './VcsImportSidebar.module.less';

/** The fossabot install walkthrough on the docs site. Its content is tracked by CORE-7506. */
export const FOSSABOT_SETUP_GUIDE_URL = 'https://docs.fossa.com/docs/fossabot/setup';

/**
 * Per-app sidebar content: what the app is and what it asks GitHub for, in the order the
 * design lists them. fossaCore is the read-role FOSSA GitHub app that migrated orgs hold.
 */
const APP_CONTENT: Record<VcsApp, { about: string; permissions: string[] }> = {
  fossabot: {
    about:
      'The fossabot GitHub app enables FOSSA to open pull requests on your behalf, run breaking ' +
      'change analysis, and track fix status — directly from the issues drawer.',
    permissions: [
      'Read & write PRs',
      'Read repository contents',
      'Read metadata',
    ],
  },
  fossaCore: {
    about: 'The FOSSA GitHub app lets FOSSA read your repositories to import and analyze your projects.',
    permissions: [
      'Read repository contents',
      'Read metadata',
    ],
  },
};

type SectionProps = {
  icon: IconClassName;
  title: string;
  /** The last section carries no rule, since nothing follows it but the setup guide. */
  hasDivider?: boolean;
};

/** One titled block of the sidebar, with a rule under it separating it from the next. */
const Section: FunctionComponent<SectionProps> = ({ icon, title, hasDivider = true, children }) => (
  <section className={hasDivider ? styles.sectionWithDivider : styles.section}>
    <h2 className={styles.sectionTitle}>
      <span className={styles.sectionIcon}><Icon icon={icon} /></span>
      {title}
    </h2>
    {children}
  </section>
);

type Props = {
  /** The app of the active account; the About/Permissions copy describes this app. */
  app: VcsApp;
  isFossabotInstalled: boolean;
  isFossaCoreInstalled: boolean;
  shouldSubmitBadgePRs: boolean;
  onToggleSubmitBadgePRs: () => void;
};

/**
 * Left column of the vcs import page: what the active account's app is, what it asks for, how
 * the two GitHub apps differ, and where to read more. It shows in both states, with each app's
 * badge reading from its own install state and the badge-PR opt-out only once any app is
 * installed.
 */
export const VcsImportSidebar: VoidFunctionComponent<Props> = ({
  app,
  isFossabotInstalled,
  isFossaCoreInstalled,
  shouldSubmitBadgePRs,
  onToggleSubmitBadgePRs,
}) => (
  <aside className={styles.sidebar}>
    <Section icon={icons.circleInfo} title="About this app">
      <p className={styles.body}>{APP_CONTENT[app].about}</p>
    </Section>

    <Section icon={icons.keyRegular} title="Permissions">
      <ul className={styles.permissions}>
        {APP_CONTENT[app].permissions.map((permission) => (
          <li key={permission} className={styles.permission}>
            <span className={styles.permissionIcon}><Icon icon={icons.checkOutline} /></span>
            <span>{permission}</span>
          </li>
        ))}
      </ul>
    </Section>

    <Section icon={icons.keyRegular} title="Two GitHub apps" hasDivider={false}>
      <p className={styles.body}>FOSSA uses two separate GitHub apps for different workflows:</p>
      <div className={styles.app}>
        <span className={styles.appIcon}><Icon icon={icons.github} /></span>
        <div className={styles.appText}>
          <div className={styles.appNameRow}>
            <span className={styles.appName}>FOSSA GitHub app</span>
            {/* Static: it reports the install state, it is not the control that installs. */}
            {isFossaCoreInstalled && (
              <Badge color="light-green" size="micro">Installed</Badge>
            )}
          </div>
          <p className={styles.appNote}>Already installed if you’ve connected a project.</p>
        </div>
      </div>
      <div className={styles.app}>
        <img
          className={styles.appLogo}
          src={VCS_APP_DISPLAY.fossabot.logo}
          alt=""
        />
        <div className={styles.appText}>
          <div className={styles.appNameRow}>
            <span className={styles.appName}>fossabot app</span>
            {/* Static: it reports the install state, it is not the control that installs. */}
            <Badge color="light-green" size="micro">
              {isFossabotInstalled ? 'Installed' : 'Install'}
            </Badge>
          </div>
          <p className={styles.appNote}>Opens PRs &amp; runs analysis</p>
        </div>
      </div>
    </Section>

    {app === 'fossabot' && (
      <a
        className={styles.guide}
        href={FOSSABOT_SETUP_GUIDE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className={styles.guideIcon}><Icon icon={icons.bookRegular} /></span>
        fossabot setup guide
      </a>
    )}

    {(isFossabotInstalled || isFossaCoreInstalled) && (
      <LabeledCheckbox
        className={styles.badgePRs}
        label="Submit badge PRs after import (public GitHub READMEs only)"
        isChecked={shouldSubmitBadgePRs}
        onChange={onToggleSubmitBadgePRs}
        testId="submit-badge-prs-checkbox"
      />
    )}
  </aside>
);
